/**
 * Zod schema for the Dukanti product create/edit form (F-003).
 *
 * The form holds human-authored fields. The schema:
 *   - trims + length-bounds `name` and `description`
 *   - rejects bidi-override control characters per design §11 X2 (W5),
 *     so users see a translated `nameInvalid` / `descriptionInvalid`
 *     instead of a raw 23514 CHECK violation from Postgres
 *   - parses the raw price string via `parseLocalePrice` keyed to the
 *     active locale (design §15 D6) and enforces positive, ≤ 2-decimal,
 *     and ≤ 10_000_000 (agorot overflow guard) rules
 *   - forces integer ≥ 0 inventory
 *   - caps `images` at `MAX_PRODUCT_IMAGES` (5)
 *   - validates each image via the shared product-image validator
 *
 * The error messages are i18n *keys* — the form renders them through
 * `t()` before display. Keys are a strict subset of `ProductErrorKey`
 * so the form stays in lockstep with `mapProductError`.
 */
import { z } from 'zod';
import {
  MAX_PRODUCT_IMAGES,
  displayToCents,
  parseLocalePrice,
  validateProductImageFile,
  validateBarcode,
  normalizeBarcode,
  type SupportedLocale,
} from '@dukanti/shared';

/** Unicode ranges used by Postgres CHECK `products_name_no_bidi_override_chk`.
 *  Explicit Arabic-script letters are intentionally NOT in this set — the
 *  check targets directional-override control chars only. */
const BIDI_OVERRIDE_RE = /[\u202A-\u202E\u2066-\u2069]/;
const MAX_VARIANT_OPTIONS = 3;
const MAX_VARIANTS = 100;

function parseValidPriceCents(
  raw: string,
  locale: SupportedLocale,
): { valid: true; cents: number } | { valid: false } {
  const parsed = parseLocalePrice(raw, locale);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { valid: false };
  }

  const cents = displayToCents(parsed);
  if (Math.abs(cents / 100 - parsed) > 1e-9) {
    return { valid: false };
  }

  if (parsed > 10_000_000) {
    return { valid: false };
  }

  return { valid: true, cents };
}

/**
 * Build the Zod schema bound to a specific locale so the price field's
 * `parseLocalePrice` call reflects what the user actually sees.
 *
 * The returned schema's inferred type is exposed as `ProductFormValues`
 * below — NOT from this function, because `z.infer` can't "close over"
 * the locale closure. Instead we define a representative schema via
 * `createProductFormSchema('en')` for the type alias; the runtime
 * validator is obtained per-render inside `ProductForm`.
 */
export function createProductFormSchema(locale: SupportedLocale) {
  const variantOptionValueSchema = z.object({
    id: z.string().optional(),
    value: z.string().trim(),
    displayOrder: z.number().int().min(0),
  });

  const variantOptionSchema = z.object({
    id: z.string().optional(),
    name: z.string().trim(),
    displayOrder: z.number().int().min(0),
    values: z.array(variantOptionValueSchema),
  });

  const variantRowSchema = z
    .object({
      id: z.string().optional(),
      optionValueIds: z.array(z.string()),
      optionSummary: z.string().default(''),
      priceRaw: z
        .string()
        .min(1, { message: 'admin.products.errors.priceRequired' })
        .superRefine((raw, ctx) => {
          if (!parseValidPriceCents(raw, locale).valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'admin.products.errors.priceInvalid',
            });
          }
        }),
      saleEnabled: z.boolean().default(false),
      salePriceRaw: z.string().default(''),
      inventory: z
        .number({
          invalid_type_error: 'admin.products.errors.inventoryInvalid',
        })
        .int({ message: 'admin.products.errors.inventoryInvalid' })
        .min(0, { message: 'admin.products.errors.inventoryInvalid' }),
      active: z.boolean().default(true),
      isDefault: z.boolean().default(false),
      imageId: z.string().nullable().default(null),
      barcodeValue: z.string().default(''),
      previousBarcodeNormalized: z.string().nullable().default(null),
      barcodeReplaceConfirmed: z.boolean().default(false),
      barcodeClearConfirmed: z.boolean().default(false),
      barcodeDeletedReuseConfirmedNormalized: z.string().nullable().default(null),
    })
    .superRefine((variant, ctx) => {
      const barcodeRaw = variant.barcodeValue.trim();
      if (barcodeRaw.length > 0) {
        const barcode = validateBarcode(variant.barcodeValue);
        if (!barcode.valid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: barcode.errorKey ?? 'admin.products.errors.barcodeInvalid',
            path: ['barcodeValue'],
          });
        } else if (
          variant.barcodeDeletedReuseConfirmedNormalized !== null &&
          variant.barcodeDeletedReuseConfirmedNormalized !== barcode.normalized
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.errors.barcodeDeletedConflict',
            path: ['barcodeValue'],
          });
        } else if (
          variant.previousBarcodeNormalized &&
          variant.previousBarcodeNormalized !== barcode.normalized &&
          !variant.barcodeReplaceConfirmed
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.errors.barcodeReplaceConfirmationRequired',
            path: ['barcodeValue'],
          });
        }
      } else if (variant.barcodeDeletedReuseConfirmedNormalized !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.products.errors.barcodeDeletedConflict',
          path: ['barcodeValue'],
        });
      } else if (variant.previousBarcodeNormalized && !variant.barcodeClearConfirmed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.products.errors.barcodeClearConfirmationRequired',
          path: ['barcodeValue'],
        });
      }

      if (!variant.saleEnabled) return;

      const regular = parseValidPriceCents(variant.priceRaw, locale);
      const sale = parseValidPriceCents(variant.salePriceRaw, locale);

      if (!sale.valid || (regular.valid && sale.cents >= regular.cents)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.products.errors.salePriceInvalid',
          path: ['salePriceRaw'],
        });
      }
    });

  return z
    .object({
      name: z
        .string()
        .trim()
        .min(1, { message: 'admin.products.errors.nameRequired' })
        .max(200, { message: 'admin.products.errors.nameTooLong' })
        .refine((s) => !BIDI_OVERRIDE_RE.test(s), {
          message: 'admin.products.errors.nameInvalid',
        }),

      categoryId: z.string().trim().min(1, {
        message: 'admin.products.errors.categoryRequired',
      }),

      description: z
        .string()
        .trim()
        .max(2000, { message: 'admin.products.errors.descriptionInvalid' })
        .refine((s) => !BIDI_OVERRIDE_RE.test(s), {
          message: 'admin.products.errors.descriptionInvalid',
        })
        .optional()
        .default(''),

      /** Raw user-typed string. Parsed via parseLocalePrice + cents rules below. */
      priceRaw: z
        .string()
        .min(1, { message: 'admin.products.errors.priceRequired' })
        .superRefine((raw, ctx) => {
          if (!parseValidPriceCents(raw, locale).valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'admin.products.errors.priceInvalid',
            });
          }
        }),

      saleEnabled: z.boolean().default(false),
      salePriceRaw: z.string().default(''),

      /** Non-negative integer. Empty string is coerced to 0 in the form. */
      inventory: z
        .number({
          invalid_type_error: 'admin.products.errors.inventoryInvalid',
        })
        .int({ message: 'admin.products.errors.inventoryInvalid' })
        .min(0, { message: 'admin.products.errors.inventoryInvalid' }),

      /** Ordered list of File objects staged for upload. Validated per-item. */
      images: z
        .array(
          z.custom<File>((file) => file instanceof File, {
            message: 'admin.products.errors.imageInvalidType',
          }),
        )
        .max(MAX_PRODUCT_IMAGES, {
          message: 'admin.products.errors.tooManyImages',
        })
        .superRefine((files, ctx) => {
          files.forEach((file, idx) => {
            const verdict = validateProductImageFile(file);
            if (verdict === 'imageTooLarge') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'admin.products.errors.imageTooLarge',
                path: [idx],
              });
            } else if (verdict === 'imageInvalidType') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'admin.products.errors.imageInvalidType',
                path: [idx],
              });
            }
          });
        })
        .default([]),

      mode: z.enum(['simple', 'options']).default('simple'),
      options: z.array(variantOptionSchema).default([]),
      variants: z.array(variantRowSchema).default([]),
    })
    .superRefine((values, ctx) => {
      if (values.saleEnabled) {
        const regular = parseValidPriceCents(values.priceRaw, locale);
        const sale = parseValidPriceCents(values.salePriceRaw, locale);

        if (!sale.valid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.errors.salePriceInvalid',
            path: ['salePriceRaw'],
          });
        } else if (regular.valid && sale.cents >= regular.cents) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.errors.salePriceInvalid',
            path: ['salePriceRaw'],
          });
        }
      }

      if (values.mode === 'simple') return;

      if (values.options.length > MAX_VARIANT_OPTIONS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.products.variants.errors.tooManyOptions',
          path: ['options'],
        });
      }

      if (values.variants.length > MAX_VARIANTS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.products.variants.errors.tooManyVariants',
          path: ['variants'],
        });
      }

      values.options.forEach((option, optionIndex) => {
        if (option.name.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.variants.errors.optionNameRequired',
            path: ['options', optionIndex, 'name'],
          });
        }

        const seenValues = new Set<string>();
        option.values.forEach((value, valueIndex) => {
          const normalizedValue = value.value.trim().toLocaleLowerCase();
          if (normalizedValue.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'admin.products.variants.errors.optionValueRequired',
              path: ['options', optionIndex, 'values', valueIndex, 'value'],
            });
            return;
          }

          if (seenValues.has(normalizedValue)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'admin.products.variants.errors.duplicateOptionValue',
              path: ['options', optionIndex, 'values', valueIndex, 'value'],
            });
          }
          seenValues.add(normalizedValue);
        });
      });

      if (!values.variants.some((variant) => variant.active)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'admin.products.variants.errors.noActiveVariant',
          path: ['variants'],
        });
      }

      const activeSignatures = new Set<string>();
      values.variants.forEach((variant, variantIndex) => {
        if (!variant.active) return;
        const signature = [...variant.optionValueIds].sort().join('|');
        if (activeSignatures.has(signature)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.variants.errors.duplicateCombination',
            path: ['variants', variantIndex, 'optionValueIds'],
          });
        }
        activeSignatures.add(signature);
      });

      const seenBarcodes = new Map<string, number>();
      values.variants.forEach((variant, variantIndex) => {
        const normalized = normalizeBarcode(variant.barcodeValue ?? '');
        if (!normalized) return;
        const firstIndex = seenBarcodes.get(normalized);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.errors.barcodeDuplicate',
            path: ['variants', variantIndex, 'barcodeValue'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'admin.products.errors.barcodeDuplicate',
            path: ['variants', firstIndex, 'barcodeValue'],
          });
        }
        seenBarcodes.set(normalized, variantIndex);
      });
    })
    .transform((values) => {
      if (values.mode === 'options') return values;

      return {
        ...values,
        mode: 'simple' as const,
        options: [],
        variants: [
          {
            id: values.variants[0]?.id,
            optionValueIds: [],
            optionSummary: '',
            priceRaw: values.priceRaw,
            saleEnabled: values.saleEnabled,
            salePriceRaw: values.salePriceRaw,
            inventory: values.inventory,
            active: true,
            isDefault: true,
            imageId: null,
            barcodeValue: values.variants[0]?.barcodeValue ?? '',
            previousBarcodeNormalized: values.variants[0]?.previousBarcodeNormalized ?? null,
            barcodeReplaceConfirmed: values.variants[0]?.barcodeReplaceConfirmed ?? false,
            barcodeClearConfirmed: values.variants[0]?.barcodeClearConfirmed ?? false,
            barcodeDeletedReuseConfirmedNormalized:
              values.variants[0]?.barcodeDeletedReuseConfirmedNormalized ?? null,
          },
        ],
      };
    });
}

/** Form values type. All locale-bound schemas share this shape, so
 *  inferring from one (any) locale gives the canonical shape. */
export type ProductFormValues = z.infer<ReturnType<typeof createProductFormSchema>>;
